import React from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';

interface PricingTierProps {
  title: string;
  price: string;
  description: string;
  features: string[];
  isPopular?: boolean;
  ctaText: string;
}

const PricingTier: React.FC<PricingTierProps> = ({ 
  title, 
  price, 
  description, 
  features, 
  isPopular = false, 
  ctaText 
}) => {
  return (
    <div className={`relative bg-white rounded-lg shadow-lg p-6 ${isPopular ? 'ring-2 ring-yellow-500' : ''}`}>
      {isPopular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-yellow-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
            Most Popular
          </span>
        </div>
      )}
      
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        <div className="mt-2">
          <span className="text-3xl font-bold text-gray-900">{price}</span>
          <span className="text-gray-600">/month</span>
        </div>
        <p className="text-gray-600 mt-2">{description}</p>
      </div>
      
      <ul className="space-y-3 mb-6">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <CheckIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
            <span className="text-gray-700">{feature}</span>
          </li>
        ))}
      </ul>
      
      <button 
        className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
          isPopular 
            ? 'bg-yellow-500 text-white hover:bg-yellow-600' 
            : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
        }`}
      >
        {ctaText}
      </button>
    </div>
  );
};

const PricingSection: React.FC = () => {
  const pricingTiers = [
    {
      title: "Basic Listing",
      price: "Free",
      description: "Perfect for small-scale beekeepers",
      features: [
        "Basic profile listing",
        "Contact information display",
        "Location on map",
        "Standard search visibility"
      ],
      ctaText: "Get Started Free"
    },
    {
      title: "Premium Seller",
      price: "$29",
      description: "Enhanced visibility and features",
      features: [
        "Priority search placement",
        "Gold star premium badge",
        "Enhanced profile with photos",
        "Customer reviews & ratings",
        "Direct contact information",
        "Featured in directory",
        "Monthly analytics report"
      ],
      isPopular: true,
      ctaText: "Start Premium Trial"
    },
    {
      title: "Commercial Business",
      price: "$79",
      description: "Full-featured business solution",
      features: [
        "Everything in Premium",
        "Multiple location listings",
        "Bulk inventory management",
        "Advanced analytics dashboard",
        "Custom branding options",
        "API access for integration",
        "Priority customer support",
        "Advertising opportunities"
      ],
      ctaText: "Contact Sales"
    }
  ];

  return (
    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Choose Your Membership Level
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Join thousands of beekeepers connecting buyers and sellers across the South. 
            Choose the plan that fits your business needs.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {pricingTiers.map((tier, index) => (
            <PricingTier key={index} {...tier} />
          ))}
        </div>
        
        <div className="text-center mt-8">
          <p className="text-gray-600">
            All plans include access to our network of verified beekeepers and secure transaction processing.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;